import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';
import dynamic from 'next/dynamic';

import { Badge, ClockIcon, IconText, LocationIcon } from '@motorway/mw-highway-code';
import { VRM } from '@motorway/mw-highway-code/alpha';

import cypressIds from 'Components/cypress_ids.json';
import { NumericGradingText } from 'Components/NumericGradingText/NumericGradingText';
import { ExLeaseBadge } from 'Components/shared/ExLeaseBadge';
import LazyLoadImage from 'Components/shared/LazyLoadImage/LazyLoadImage';
import { MobilizeBadge } from 'Components/shared/MobilizeBadge';
import VehicleHighestBid from 'Components/shared/VehicleCards/VehicleHighestBid/VehicleHighestBid';
import texts from 'Components/texts.json';
import VehicleCardShortlistButton from 'Components/VehicleList/VehicleListShortlistButton/VehicleListShortlistButton';
import { FeaturesContext } from 'Context/FeaturesContext/FeaturesContext';
import { VehicleContext } from 'Context/VehicleContext';
import { WithVehiclePermitsChecker } from 'Controllers/VehiclePermitsChecker/VehiclePermitsChecker.helpers';
import { EarlyDocumentsBadge } from 'Features/EarlyDocuments';
import { PaidListingInfoIcon } from 'Features/PaidListings';
import { triggerVehicleGradeHoverEvent } from 'Services/analytics/events';
import { applySessionReplayMask } from 'Services/datadog/datadogRum';
import { MASKED_EVENT_VALUES } from 'Services/datadog/datadogRum.const';
import { useListType } from 'Stores/ListStore/ListStore';
import { useIsUnshortlisted } from 'Stores/ShortlistStore/ShortlistStore';
import { ForSaleCategory } from 'Types/enquiryStates';
import { LIST_TYPES } from 'Utilities/consts';
import {
	autoShorthand,
	formatDistance,
	formatMake,
	formatText,
	formatTransmission,
	formatVehicleName,
	formatVehiclePrice,
	fuelTypeShorthand,
} from 'Utilities/formatters';
import { applyCypressData } from 'Utilities/helpers';
import { calculateShouldVehicleShowOriginalPrice } from 'Utilities/helpers/calculateShouldVehicleShowOriginalPrice';
import { formatVehicleOriginalPrice } from 'Utilities/helpers/formatVehicleOriginalPrice';
import { useCountdownTimer } from 'Utilities/hooks/useCountdownTimer';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import { useIsBinVehicleProcessing } from 'Utilities/hooks/vehicle/useIsBinVehicleProcessing';
import proPropTypes from 'Utilities/proPropTypes';

import VehicleCardBidCount from '../VehicleCardBidCount/VehicleCardBidCount';
import VehicleCardGallery from '../VehicleCardGallery/VehicleCardGallery';
import { VehiclePrice } from '../VehiclePrice/VehiclePrice';

import { useBidCount } from './VehicleCardView.helpers';

import styles from './VehicleCardView.module.scss';

T.setTexts(texts);

const StateExplorer = dynamic(
	() =>
		import(/* webpackChunkName: "state_explorer" */ 'Components/FeaturesEditor/plugins/StateExplorer/StateExplorer'),
);

const { vehicle: ddVehicleValues } = MASKED_EVENT_VALUES;
const privacyParams = {
	distance: applySessionReplayMask({ maskedActionName: ddVehicleValues.distanceFrom }),
	vrm: applySessionReplayMask({ maskedActionName: ddVehicleValues.vrm }),
};

const VehicleCardView = ({
	className,
	displayPaidListingIcon,
	displayShortListButton = true,
	imageWidth = 336,
	onClickShortlist,
	page,
	permits,
	showValue = true,
}) => {
	const isBinExpiryVisibilityEnabled = useFeatureToggle(FEATURES.binExpiryVisibility);
	const isBinDiscountVisibilityEnabled = useFeatureToggle(FEATURES.binDiscountVisibility);
	const isLiveSaleDiscountVisibilityEnabled = useFeatureToggle(FEATURES.liveSaleDiscountVisibility);
	const isExLease = useFeatureToggle(FEATURES.exLeaseBranding);
	const isVehicleListHeroImagesEnabled = useFeatureToggle(FEATURES.vehicleListHeroImages);

	const vehicle = useContext(VehicleContext);
	const features = useContext(FeaturesContext);
	const {
		distance,
		expiryDate,
		fuel,
		hasEarlyDocuments,
		heroImages,
		id,
		isPaidListing,
		isTrade,
		leadPhoto,
		make,
		mileage,
		model,
		numericGrade,
		originalPrice,
		prettyVrm,
		reheatCount,
		sourcePartner,
		state,
		transmission,
		year,
	} = vehicle;
	const { slug } = state ?? {};

	const vehicleName = formatVehicleName({ make, model });
	const vehiclePhotoAlt = `${vehicleName} ${year}`;

	const listType = useListType();
	const isShortlistListType = listType === LIST_TYPES.shortlist;
	const isUnshortlisted = useIsUnshortlisted(id);
	const shouldApplyFadeOutStyle = isShortlistListType && isUnshortlisted;

	const { bidCount, shouldShowBidCount } = useBidCount({ id, state });

	const { shouldShowCountdown, timeRemainingText } = useCountdownTimer(expiryDate);
	const isBinCountdownEnabled = isBinExpiryVisibilityEnabled && slug === ForSaleCategory.forSale;
	const shouldShowBinExpiry = isBinCountdownEnabled && shouldShowCountdown && timeRemainingText;

	const isBinVehicleProcessing = useIsBinVehicleProcessing({ expiryDate, slug });
	const isMobilizeVehicle = sourcePartner?.toLowerCase() === 'mobilize';

	const { displayHighestBid, getDisplayPrice, hasDisplayPrice } = permits;

	const { label, showVAT, value: price } = getDisplayPrice();
	const vehicleOriginalPrice = formatVehicleOriginalPrice({ originalPrice, price, slug });

	const shouldShowOriginalPrice = calculateShouldVehicleShowOriginalPrice({
		isBinDiscountVisibilityEnabled,
		isLiveSaleDiscountVisibilityEnabled,
		slug,
		vehicleOriginalPrice,
	});

	const [priceLabel, vehiclePrice] = formatVehiclePrice(label, price, showVAT);
	const vehiclePriceArgs = {
		originalPrice: shouldShowOriginalPrice ? vehicleOriginalPrice : '',
		price: vehiclePrice,
		reheatCount,
	};

	return (
		<div className={cx(styles.vehicleListCardView, className)} data-testid="vehicleCardLink">
			<div className={styles.vehicleListCardImageContainer}>
				{displayPaidListingIcon && isPaidListing ? (
					<PaidListingInfoIcon />
				) : (
					hasEarlyDocuments && (
						<EarlyDocumentsBadge hasEarlyDocuments={!!hasEarlyDocuments} id={id} isPaidListing={isPaidListing} />
					)
				)}
				{displayShortListButton && (
					<VehicleCardShortlistButton
						onClickShortlist={onClickShortlist}
						page={page}
						shortlistButtonClassName={styles.shortlistButton}
					/>
				)}
				{isVehicleListHeroImagesEnabled && heroImages?.length > 1 ? (
					<VehicleCardGallery
						enquiryId={id}
						heroImages={heroImages}
						imageWidth={imageWidth}
						leadPhoto={leadPhoto}
						shouldApplyFadeOutStyle={shouldApplyFadeOutStyle}
						vehiclePhotoAlt={vehiclePhotoAlt}
					/>
				) : (
					<LazyLoadImage
						alt={vehiclePhotoAlt}
						classNames={{
							image: cx(styles?.vehicleListCardImage, {
								[styles?.fadeOut]: shouldApplyFadeOutStyle,
							}),
						}}
						cypressId={cypressIds.images.vehicleCard}
						height={imageWidth * 0.75}
						photo={leadPhoto}
						rootComponent="VehicleListCardView"
						width={imageWidth}
					/>
				)}
				{isMobilizeVehicle && <MobilizeBadge />}
				{isExLease && isTrade && !isMobilizeVehicle && <ExLeaseBadge />}
				<div className={styles.badgeContainer}>
					{shouldShowBinExpiry && <Badge reverse icon={ClockIcon} text={timeRemainingText} variant="error" />}
					{isBinVehicleProcessing && <Badge text={T.translate('slugs.processing')} variant="warning" />}
				</div>
			</div>
			<div className={styles.detailsContainer}>
				<div className={styles.topInfoBar}>
					<div {...privacyParams.vrm} {...applyCypressData(cypressIds.text.vehicleVrm)}>
						<VRM size="small" text={prettyVrm} />
					</div>
					<div>
						{/*	{isTrade && ( */}
						{/*		<Badge */}
						{/*			{...applyCypressData(cypressIds.text.vehicleTradeBadge)} */}
						{/*			data-trade-badge */}
						{/*			reverse={true} */}
						{/*			text={texts.trade.title} */}
						{/*			variant="primary" */}
						{/*		/> */}
						{/*	)} */}
					</div>
				</div>
				<section className={styles.vehicleInfoBar}>
					<h4 {...applyCypressData(cypressIds.text.vehicleCardTitle)}>
						<span className={styles.make}>{formatMake(make)}</span>
						<span className={styles.model}>{model}</span>
					</h4>
					<div>
						<ul className={styles.detailsList}>
							<li>{year || '-'}</li>
							<li>{`${formatText(mileage)} mi`}</li>
							<li>{fuelTypeShorthand(fuel)}</li>
							<li>{autoShorthand(formatTransmission(transmission))}</li>
							{!!distance && (
								<li {...privacyParams.distance}>
									<IconText data-icontext icon={LocationIcon} text={formatDistance(distance)} />
								</li>
							)}
							{numericGrade ? (
								<li className={styles.numericGradeListItem} onMouseEnter={() => triggerVehicleGradeHoverEvent()}>
									<NumericGradingText grade={numericGrade} />
								</li>
							) : null}
						</ul>
						{showValue && (
							<div className={styles.valueBox} data-testid="valueBox">
								{hasDisplayPrice() ? <VehiclePrice label={priceLabel} {...vehiclePriceArgs} /> : null}
								{displayHighestBid() ? <VehicleHighestBid /> : null}
								{shouldShowBidCount && <VehicleCardBidCount bidCount={bidCount} />}
							</div>
						)}
						{features[FEATURES.vehicleStateExplorer] ? <StateExplorer /> : null}
					</div>
				</section>
			</div>
		</div>
	);
};

VehicleCardView.propTypes = {
	displayPaidListingIcon: PropTypes.bool,
	displayShortListButton: PropTypes.bool,
	imageWidth: PropTypes.number,
	permits: proPropTypes.vehiclesPermitsChecker.isRequired,
	showValue: PropTypes.bool,
};

export default WithVehiclePermitsChecker()(VehicleCardView);
